/**
 * Recherche prédictive — ouverture de la modal et interrogation de l'API.
 *
 * - Ouverture : les liens [data-search-open] du header (qui pointent vers
 *   /search en secours sans JavaScript) ouvrent la <dialog> native.
 * - Saisie : debounce de 300 ms puis fetch de la Section Rendering API
 *   (routes.predictive_search_url + section_id=predictive-search) ; toute
 *   requête précédente encore en vol est annulée (AbortController).
 * - Clavier : ↑/↓ parcourent les résultats (aria-activedescendant), Entrée
 *   ouvre le résultat actif, Échap ferme (natif <dialog>).
 * - Le compteur de résultats est annoncé aux lecteurs d'écran (aria-live).
 */
const DEBOUNCE_MS = 300;

/** Délai max d'une requête de recherche avant abandon (invariant réseau §1). */
const SEARCH_TIMEOUT_MS = 10000;

class SearchModal extends HTMLElement {
  #dialog = null;
  #input = null;
  #results = null;
  #status = null;
  #controller = null;
  #timer = null;
  #options = [];
  #activeIndex = -1;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#input = this.querySelector('.search-modal__input');
    this.#results = this.querySelector('.search-modal__results');
    this.#status = this.querySelector('[data-search-status]');
    if (!this.#dialog || !this.#input || !this.#results) return;

    document.querySelectorAll('[data-search-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.#dialog.showModal();
        this.#input.focus();
      });
    });

    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.#dialog.close());

    // Clic sur le fond assombri : la cible est alors la <dialog> elle-même
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    });

    // showModal() ne boucle PAS le Tab en fin de liste (vérifié : Chrome renvoie
    // vers <body> après le dernier élément) — piège de focus applicatif, même
    // motif que header-drawer. Posé sur la <dialog> (pas seulement l'input) pour
    // intercepter le Tab quel que soit l'élément actif (input, boutons).
    this.#dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') this.#trapFocus(event);
    });

    this.#input.addEventListener('input', () => {
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#search(), DEBOUNCE_MS);
    });

    this.#input.addEventListener('keydown', (event) => this.#onKeydown(event));
  }

  async #search() {
    const query = this.#input.value.trim();

    this.#controller?.abort();

    if (query.length === 0) {
      this.#render('');
      return;
    }

    const controller = new AbortController();
    this.#controller = controller;
    // Timeout explicite : abandonne une requête qui pendrait (réseau dégradé).
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, SEARCH_TIMEOUT_MS);

    const url = new URL(this.dataset.predictiveSearchUrl, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('options[prefix]', 'last');
    // Champs interrogés : SANS ce paramètre, Shopify n'interroge que titre / type /
    // variante / vendeur — donc ni les TAGS ni la DESCRIPTION. Nos produits aux noms
    // opaques (« Sirop Or Noir », « Sable Noir ») devenaient introuvables en tapant
    // « citron noir » dans les suggestions, alors que la page /search, elle, les trouvait.
    // Mesuré sur la boutique : « citron noir » passe de 3 à 8 produits suggérés.
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,tag,body'
    );

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Recherche prédictive : HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.predictive-search');
      this.#render(section ? section.outerHTML : '');
    } catch (error) {
      // Abandon dû à une frappe suivante (requête remplacée) → silencieux.
      // Abandon dû au timeout, ou échec réseau/HTTP → message visible localisé
      // (la soumission classique du formulaire reste disponible, Entrée → /search).
      if (error.name === 'AbortError' && !timedOut) return;
      this.#showError();
    } finally {
      clearTimeout(timeout);
    }
  }

  /** Vide les résultats et affiche un message d'erreur visible + annoncé (aria-live). */
  #showError() {
    const message = this.dataset.errorText || '';
    this.#results.replaceChildren();
    this.#options = [];
    this.#activeIndex = -1;
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.removeAttribute('aria-activedescendant');
    if (message) {
      const p = document.createElement('p');
      p.className = 'search-modal__error';
      p.textContent = message;
      this.#results.appendChild(p);
    }
    if (this.#status) this.#status.textContent = message;
  }

  #render(html) {
    this.#results.innerHTML = html;
    this.#options = Array.from(this.#results.querySelectorAll('[role="option"]'));
    this.#activeIndex = -1;
    this.#input.setAttribute('aria-expanded', this.#options.length > 0 ? 'true' : 'false');
    this.#input.removeAttribute('aria-activedescendant');

    if (this.#status) {
      this.#status.textContent = this.#results.querySelector('[data-status]')?.textContent.trim() || '';
    }
  }

  #onKeydown(event) {
    if (this.#options.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#activate((this.#activeIndex + 1) % this.#options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#activate((this.#activeIndex - 1 + this.#options.length) % this.#options.length);
        break;
      case 'Enter':
        if (this.#activeIndex >= 0) {
          event.preventDefault();
          this.#options[this.#activeIndex].querySelector('a')?.click();
        }
        break;
      case 'Escape':
        break;
      default:
        return;
    }
  }

  #trapFocus(event) {
    // Les liens de résultats portent tabindex="-1" (navigation par flèches, pas
    // par Tab) : ne pas les compter, sous peine de piéger le focus sur un élément
    // que Tab n'atteint jamais en pratique.
    const focusable = Array.from(
      this.#dialog.querySelectorAll('input, button:not([disabled]), a[href]:not([tabindex="-1"])')
    ).filter((el) => el.getClientRects().length > 0);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #activate(index) {
    this.#options.forEach((option, i) => {
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    this.#activeIndex = index;
    const active = this.#options[index];
    this.#input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }
}

if (!customElements.get('search-modal')) {
  customElements.define('search-modal', SearchModal);
}
